import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { Plus, Edit2, X, Search, Download, Upload, FileDown, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatAadhar, formatMultiAadhar } from '../utils/format';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

const JOB_TITLES = ['Outlet Manager', 'Steward', 'Cleaner', 'Chef'];

// Convert "HH:MM" (24h) to "h:MM AM/PM"
const to12 = (hhmm) => {
  if (!hhmm || !hhmm.includes(':')) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const shiftHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) diff += 24 * 60; // overnight shift
  return Math.round((diff / 60) * 100) / 100;
};

const shiftLabel = (start, end) => {
  if (!start || !end) return '—';
  return `${to12(start)} – ${to12(end)} (${shiftHours(start, end)} hrs)`;
};

const emptyForm = {
  name: '', mobile: '', jobTitle: '', aadharCard: '', parentsAadharCard: '', emergencyContact: '',
  type: 'fresher', experience: '', qualification: '',
  dateOfJoining: '', shiftStart: '08:00', shiftEnd: '18:00',
  salary: '', outlet: '', photo: null, facePhotos: [],
  lastWorkingDay: '', leftWithoutNotice: false,
};

const staffToForm = (s) => ({
  name: s.name, mobile: s.mobile || '', jobTitle: s.jobTitle || '',
  aadharCard: formatAadhar(s.aadharCard) || '',
  parentsAadharCard: formatMultiAadhar(s.parentsAadharCard) || '',
  emergencyContact: s.emergencyContact || '',
  type: s.type, experience: s.experience || '', qualification: s.qualification || '',
  dateOfJoining: s.dateOfJoining?.split('T')[0] || '',
  shiftStart: s.shiftStart, shiftEnd: s.shiftEnd,
  salary: s.salary, outlet: s.outlet?._id || '', photo: null, facePhotos: s.facePhotos || [],
  lastWorkingDay: s.lastWorkingDay?.split('T')[0] || '',
  leftWithoutNotice: s.leftWithoutNotice || false,
});

// Shared form fields for both create and edit
function StaffFields({ form, setForm, outlets, isEdit }) {
  return (
    <>
      <div className="form-group">
        <label>Full Name *</label>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </div>
      <div className="form-group">
        <label>Staff Mobile Number *</label>
        <div className="input-group">
          <span className="input-prefix">+91</span>
          <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} maxLength={10} inputMode="numeric" required />
        </div>
      </div>
      <div className="form-group">
        <label>Job Title *</label>
        <select value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} required>
          <option value="">Select job title</option>
          {JOB_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Outlet (Job Location) *</label>
        <select value={form.outlet} onChange={(e) => setForm({ ...form, outlet: e.target.value })} required>
          <option value="">Select outlet</option>
          {outlets.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Aadhar Card *</label>
        <input value={form.aadharCard} onChange={(e) => setForm({ ...form, aadharCard: e.target.value.replace(/\D/g, '').slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1-') })} maxLength={14} inputMode="numeric" placeholder="0000-0000-0000" required />
      </div>
      <div className="form-group">
        <label>Parent's Aadhar Card <span className="text-muted">(comma-separate for multiple)</span></label>
        <input value={form.parentsAadharCard} onChange={(e) => setForm({ ...form, parentsAadharCard: formatMultiAadhar(e.target.value) })} inputMode="numeric" placeholder="0000-0000-0000, 0000-0000-0000" />
      </div>
      <div className="form-group">
        <label>Emergency Contact *</label>
        <input
          value={form.emergencyContact}
          onChange={(e) => setForm({ ...form, emergencyContact: e.target.value.replace(/[^0-9,]/g, '') })}
          inputMode="numeric"
          placeholder="e.g. 9876543210,9123456789"
          required
        />
      </div>
      <div className="form-group">
        <label>Experience *</label>
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
          <option value="fresher">Fresher</option>
          <option value="experienced">Experienced</option>
        </select>
      </div>
      {form.type === 'experienced' && (
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Experience Details</label>
          <textarea value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} rows={2} />
        </div>
      )}
      <div className="form-group">
        <label>Qualification</label>
        <input value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Date of Joining *</label>
        <input type="date" value={form.dateOfJoining} onChange={(e) => setForm({ ...form, dateOfJoining: e.target.value })} required />
      </div>
      <div className="form-group">
        <label>Shift Start Time *</label>
        <input type="time" value={form.shiftStart} onChange={(e) => setForm({ ...form, shiftStart: e.target.value })} required />
      </div>
      <div className="form-group">
        <label>Shift End Time *</label>
        <input type="time" value={form.shiftEnd} onChange={(e) => setForm({ ...form, shiftEnd: e.target.value })} required />
      </div>
      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
        <div className="shift-summary">
          Shift: <strong>{shiftLabel(form.shiftStart, form.shiftEnd)}</strong>
        </div>
      </div>
      <div className="form-group">
        <label>Monthly Salary (₹) *</label>
        <input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} required />
      </div>
      <div className="form-group">
        <label>Profile Photo</label>
        <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, photo: e.target.files[0] })} />
      </div>
      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
        <label>
          Face Recognition Photos
          <span className="text-muted" style={{ fontWeight: 400, marginLeft: 6 }}>
            (up to 5 clear photos — used by the attendance kiosk to identify this person)
          </span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
          {(form.facePhotos || []).map((f, i) => {
            const isFile = f instanceof File;
            const src = isFile ? URL.createObjectURL(f) : `${API_BASE}/uploads/${f}`;
            const handleDownload = async () => {
              const filename = `face-photo-${i + 1}.jpg`;
              if (isFile) {
                const url = URL.createObjectURL(f);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 100);
                return;
              }
              try {
                const res = await fetch(src);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 100);
              } catch {
                toast.error('Could not download photo');
              }
            };
            return (
              <div key={i} style={{ flexShrink: 0 }}>
                <div style={{ position: 'relative', width: 84, height: 84 }}>
                  <img
                    src={src}
                    alt={`face ${i + 1}`}
                    style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 10, border: '2px solid #e2e8f0', display: 'block' }}
                  />
                  {/* Delete */}
                  <button
                    type="button"
                    onClick={() => { if (window.confirm('Remove this face photo?')) setForm({ ...form, facePhotos: form.facePhotos.filter((_, j) => j !== i) }); }}
                    style={{
                      position: 'absolute', top: -7, right: -7,
                      width: 22, height: 22, borderRadius: '50%',
                      background: '#ef4444', color: '#fff', border: 'none',
                      cursor: 'pointer', fontSize: 14, lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    title="Remove"
                  >×</button>
                  {/* Download */}
                  <button
                    type="button"
                    onClick={handleDownload}
                    style={{
                      position: 'absolute', bottom: 5, right: 5,
                      background: 'rgba(0,0,0,0.55)', border: 'none',
                      borderRadius: 5, padding: '3px 5px',
                      cursor: 'pointer', color: 'white',
                      display: 'flex', alignItems: 'center',
                    }}
                    title="Download"
                  ><Download size={11} /></button>
                </div>
                <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Photo {i + 1}</div>
              </div>
            );
          })}
          {(form.facePhotos || []).length < 5 && (
            <label style={{
              width: 84, height: 84, border: '2px dashed #cbd5e1', borderRadius: 10,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#94a3b8', fontSize: 12, textAlign: 'center', gap: 2,
            }}>
              <span style={{ fontSize: 22, lineHeight: 1 }}>+</span>
              <span>Add Photo</span>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const current = form.facePhotos || [];
                  const slots = 5 - current.length;
                  const newFiles = Array.from(e.target.files).slice(0, slots);
                  setForm({ ...form, facePhotos: [...current, ...newFiles] });
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
        {(form.facePhotos || []).length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>
            Add up to 5 photos of this person's face. More photos improve recognition accuracy on the kiosk.
          </p>
        )}
        {(form.facePhotos || []).length > 0 && (
          <p style={{ color: '#64748b', fontSize: 12, marginTop: 6 }}>
            {5 - (form.facePhotos || []).length} slot{5 - (form.facePhotos || []).length !== 1 ? 's' : ''} remaining.
            After saving, open the kiosk Admin panel and tap "Auto-register from uploaded photos".
          </p>
        )}
      </div>
      {isEdit && (
        <div style={{ gridColumn: '1', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={form.leftWithoutNotice || false} onChange={(e) => setForm({ ...form, leftWithoutNotice: e.target.checked })} style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ lineHeight: 1.4 }}>Left without completing notice period</span>
          </label>
          <div className="form-group">
            <label>Last Working Day</label>
            <input type="date" value={form.lastWorkingDay || ''} onChange={(e) => setForm({ ...form, lastWorkingDay: e.target.value })} />
          </div>
        </div>
      )}
    </>
  );
}

export default function StaffPage() {
  const { hasRole } = useAuth();
  const [staff, setStaff] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null); // 'outlet' | 'status' | null (default: OFF last)
  const [sortDir, setSortDir] = useState('asc');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const [viewStaff, setViewStaff] = useState(null);
  const [viewEditMode, setViewEditMode] = useState(false);
  const [viewForm, setViewForm] = useState(emptyForm);
  const [viewSaving, setViewSaving] = useState(false);

  const [faceRegStatus, setFaceRegStatus]   = useState(null); // null | 'loading' | 'success' | 'error'
  const [faceRegMessage, setFaceRegMessage] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);

  const canEdit = hasRole('super_admin', 'organization', 'outlet_owner', 'outlet_manager');

  const registerFace = async (staff) => {
    if (!staff.facePhotos?.length) {
      toast.error('Upload face photos first before registering.');
      return;
    }
    setFaceRegStatus('loading');
    setFaceRegMessage('Loading face recognition library…');
    try {
      // Load face-api.js from CDN once
      if (!window.faceapi) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1/dist/face-api.js';
          s.onload = resolve;
          s.onerror = () => reject(new Error('Failed to load face-api.js'));
          document.head.appendChild(s);
        });
      }
      const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1/model';
      if (!window._faceModelsLoaded) {
        setFaceRegMessage('Downloading recognition models (one-time, ~6 MB)…');
        await window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        await window.faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL);
        await window.faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        window._faceModelsLoaded = true;
      }
      const opts = new window.faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 });
      const descriptors = [];
      for (let i = 0; i < staff.facePhotos.length; i++) {
        setFaceRegMessage(`Processing photo ${i + 1} of ${staff.facePhotos.length}…`);
        const imgEl = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error(`Could not load photo ${i + 1}`));
          img.src = `${API_BASE}/uploads/${staff.facePhotos[i]}`;
        });
        const result = await window.faceapi
          .detectSingleFace(imgEl, opts)
          .withFaceLandmarks(true)
          .withFaceDescriptor();
        if (result) descriptors.push(Array.from(result.descriptor));
      }
      if (descriptors.length === 0) {
        setFaceRegStatus('error');
        setFaceRegMessage('No face detected in any photo. Please upload clearer, well-lit face photos.');
        return;
      }
      const avgDescriptor = descriptors[0].map((_, i) =>
        descriptors.reduce((sum, d) => sum + d[i], 0) / descriptors.length
      );
      setFaceRegMessage('Saving face data…');
      await api.put(`/staff/${staff._id}/face-descriptor`, { descriptor: avgDescriptor });
      setFaceRegStatus('success');
      setFaceRegMessage(`Registered using ${descriptors.length} of ${staff.facePhotos.length} photo(s). Staff can now scan at the kiosk.`);
      setViewStaff(prev => ({ ...prev, faceDescriptor: [1] }));
      fetchData();
    } catch (e) {
      setFaceRegStatus('error');
      setFaceRegMessage(e.message || 'Registration failed');
    }
  };

  const fetchData = async () => {
    const [staffRes, outletRes] = await Promise.all([
      api.get('/staff'),
      api.get('/outlets'),
    ]);
    setStaff(staffRes.data);
    setOutlets(outletRes.data);
  };

  useEffect(() => { fetchData(); }, []);

  const downloadStaffTemplate = () => {
    const headers = [
      'Name', 'Mobile', 'Job Title', 'Aadhar Card', 'Parent Aadhar', 'Emergency Contact',
      'Type', 'Experience', 'Qualification', 'Date Of Joining', 'Shift Start', 'Shift End', 'Salary', 'Outlet ID',
    ];
    const sample = [
      'John Doe', '9876543210', 'Steward', '1234-5678-9012', '', '9876500000',
      'fresher', '1 yr exp', '12th', '2026-01-15', '09:00', '18:00', '15000', outlets[0]?.outletId || 'ORG0001-01',
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, sample]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Staff');
    XLSX.writeFile(wb, 'staff_upload_template.xlsx');
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setBulkUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { raw: false, defval: '' });
      if (!rows.length) {
        toast.error('The file has no data rows');
        return;
      }
      const res = await api.post('/staff/bulk-upload', { rows });
      const { created, failed } = res.data;
      if (created) toast.success(`${created} staff member(s) imported`);
      if (failed?.length) {
        toast.error(`${failed.length} row(s) failed — e.g. row ${failed[0].row}: ${failed[0].error}`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error importing file');
    } finally {
      setBulkUploading(false);
    }
  };

  const q = search.trim().toLowerCase();
  const searchedStaff = !q ? staff : staff.filter((s) => {
    const fields = [
      s.staffId, s.name, s.outlet?.name, s.jobTitle, s.mobile,
      s.mobile ? `+91 ${s.mobile}` : '',
      s.emergencyContact, s.qualification, s.aadharCard,
    ];
    return fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  const filteredStaff = [...searchedStaff].sort((a, b) => {
    if (sortKey === 'outlet') {
      const cmp = (a.outlet?.name || '').localeCompare(b.outlet?.name || '');
      return sortDir === 'asc' ? cmp : -cmp;
    }
    if (sortKey === 'salary') {
      const cmp = (a.salary || 0) - (b.salary || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    }
    if (sortKey === 'status') {
      const av = a.isActive === false ? 1 : 0;
      const bv = b.isActive === false ? 1 : 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    // default: active (ON) first, inactive (OFF) at the bottom
    return (a.isActive === false ? 1 : 0) - (b.isActive === false ? 1 : 0);
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const buildFormData = (src) => {
    const data = new FormData();
    Object.entries(src).forEach(([k, v]) => {
      if (k === 'facePhotos') {
        data.append('manageFacePhotos', 'true');
        if (Array.isArray(v)) v.forEach(f => {
          if (f instanceof File) data.append('facePhotos', f);
          else if (typeof f === 'string') data.append('existingFacePhotos', f);
        });
      } else if (v !== null && v !== undefined && v !== '') {
        data.append(k, v);
      }
    });
    return data;
  };

  // ----- Create -----
  const openCreate = () => { setForm(emptyForm); setShowModal(true); };
  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/staff', buildFormData(form));
      toast.success('Staff created');
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving staff');
    } finally {
      setLoading(false);
    }
  };

  // ----- View / Edit -----
  const openView = (s) => { setViewStaff(s); setViewEditMode(false); setFaceRegStatus(null); setFaceRegMessage(''); };
  const startViewEdit = () => { setViewForm(staffToForm(viewStaff)); setViewEditMode(true); };

  const isViewDirty = () => {
    if (!viewEditMode) return false;
    const pristine = staffToForm(viewStaff);
    const { photo: p1, facePhotos: fp1, ...a } = viewForm;
    const { photo: p2, facePhotos: fp2, ...b } = pristine;
    if (p1) return true;
    if (fp1?.length !== fp2?.length) return true;
    if (fp1?.some(f => f instanceof File)) return true;
    return JSON.stringify(a) !== JSON.stringify(b);
  };

  const saveViewEdit = async () => {
    setViewSaving(true);
    try {
      const res = await api.put(`/staff/${viewStaff._id}`, buildFormData(viewForm));
      toast.success('Staff updated');
      // repopulate outlet for display
      const updated = res.data;
      if (updated.outlet && typeof updated.outlet === 'string') {
        updated.outlet = outlets.find((o) => o._id === updated.outlet) || viewStaff.outlet;
      }
      setViewStaff(updated);
      setViewEditMode(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating');
    } finally {
      setViewSaving(false);
    }
  };

  const attemptCloseView = () => {
    if (isViewDirty()) {
      const save = window.confirm('You have unsaved changes. Do you want to save them?\n\nOK = Save   |   Cancel = Discard');
      if (save) { saveViewEdit(); return; }
    }
    setViewStaff(null);
    setViewEditMode(false);
  };

  // ----- Delete -----
  const downloadStaffData = async (s) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const fromDate = s.dateOfJoining?.split('T')[0] || todayStr;
    const toDate = s.lastWorkingDay?.split('T')[0] || todayStr;

    let attendance = [];
    let performance = null;
    try {
      const [attRes, perfRes] = await Promise.all([
        api.get('/attendance', { params: { staffId: s._id, from: fromDate, to: toDate } }),
        api.get(`/performance/${s._id}`, { params: { from: fromDate, to: toDate } }),
      ]);
      attendance = attRes.data || [];
      performance = perfRes.data;
    } catch {
      // proceed with whatever data is available
    }

    const doc = new jsPDF();
    const navy = [13, 59, 102];
    const white = [255, 255, 255];
    const pageH = 287;
    let y = 20;

    const ensureSpace = (need = 8) => {
      if (y + need > pageH) { doc.addPage(); y = 20; }
    };
    const heading = (text) => {
      ensureSpace(12);
      doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.setTextColor(13, 59, 102);
      doc.text(text, 14, y);
      y += 7;
      doc.setDrawColor(228, 237, 246); doc.setLineWidth(0.5);
      doc.line(14, y - 4, 196, y - 4);
    };
    const line = (label, value) => {
      ensureSpace(7);
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(String(label), 16, y);
      doc.setTextColor(15, 23, 42); doc.setFont('helvetica', 'bold');
      doc.text(String(value), 90, y);
      y += 7;
    };

    // Header bar
    doc.setFillColor(...navy);
    doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('CloudStay', 14, 12);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Staff Data Export', 14, 21);
    doc.text(`Generated: ${todayStr}`, 196, 21, { align: 'right' });
    y = 38;

    // Profile
    heading('Staff Profile');
    line('Staff ID', s.staffId);
    line('Name', s.name);
    line('Mobile', s.mobile ? `+91 ${s.mobile}` : '—');
    line('Job Title', s.jobTitle || '—');
    line('Outlet', s.outlet?.name || '—');
    line('Aadhar Card', formatAadhar(s.aadharCard) || '—');
    line('Parent Aadhar', formatMultiAadhar(s.parentsAadharCard) || '—');
    line('Emergency Contact', s.emergencyContact || '—');
    line('Type', s.type || '—');
    line('Experience', s.experience || '—');
    line('Qualification', s.qualification || '—');
    line('Date of Joining', s.dateOfJoining?.split('T')[0] || '—');
    line('Shift', shiftLabel(s.shiftStart, s.shiftEnd));
    line('Salary', `Rs ${s.salary?.toLocaleString()}`);
    line('Status', s.isActive === false ? 'Inactive' : 'Active');
    line('Last Working Day', s.lastWorkingDay?.split('T')[0] || '—');
    line('Left Without Notice', s.leftWithoutNotice ? 'Yes' : 'No');
    y += 4;

    // Increment History
    heading('Increment History');
    if (s.incrementHistory?.length) {
      s.incrementHistory.forEach((inc) => {
        line(inc.date?.split('T')[0] || '—', `Rs ${inc.oldSalary?.toLocaleString()} -> Rs ${inc.newSalary?.toLocaleString()}`);
      });
    } else {
      line('—', 'No increments recorded');
    }
    y += 4;

    // Performance
    heading(`Performance (${fromDate} to ${toDate})`);
    if (performance) {
      line('Overall Rating', performance.overallRating != null ? `${performance.overallRating} / 5` : '—');
      (performance.categories || []).forEach((c) => {
        line(c.label, c.rating != null ? `${c.rating} / 5 (${c.pct}%)` : '—');
      });
    } else {
      line('—', 'Not available');
    }
    y += 4;

    // Attendance & Fines
    heading(`Attendance & Fines (${fromDate} to ${toDate})`);
    if (attendance.length) {
      ensureSpace(7);
      doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 116, 139);
      doc.text('Date', 16, y);
      doc.text('Time In', 50, y);
      doc.text('Time Out', 80, y);
      doc.text('Hours', 110, y);
      doc.text('Status', 135, y);
      doc.text('Fine (Rs)', 165, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      [...attendance].sort((a, b) => new Date(a.date) - new Date(b.date)).forEach((a) => {
        ensureSpace(6);
        const fineTotal = (a.fines || []).reduce((sum, f) => sum + (f.amount || 0), 0);
        doc.text(a.date?.split('T')[0] || '—', 16, y);
        doc.text(a.timeIn || '—', 50, y);
        doc.text(a.timeOut || '—', 80, y);
        doc.text(String(a.hoursWorked || 0), 110, y);
        doc.text(a.status || '—', 135, y);
        doc.text(fineTotal ? String(fineTotal) : '—', 165, y);
        y += 6;
        if (a.fines?.length) {
          a.fines.forEach((f) => {
            ensureSpace(6);
            doc.setTextColor(180, 83, 9);
            doc.text(`  Fine reason: ${f.reason || '—'} (Rs ${f.amount || 0})`, 18, y);
            doc.setTextColor(15, 23, 42);
            y += 6;
          });
        }
      });
    } else {
      line('—', 'No attendance records');
    }

    doc.save(`staff_data_${s.staffId}_${s.name.replace(/\s+/g, '_')}.pdf`);
  };

  const handleDeleteStaff = async (s) => {
    if (!window.confirm(`Delete ${s.name} (${s.staffId})? This action cannot be undone.`)) return;
    if (!window.confirm('Download this staff member\'s data before deleting?\n\nOK = Download data & Delete   |   Cancel = Cancel')) return;
    try {
      await downloadStaffData(s);
      await api.delete(`/staff/${s._id}`);
      toast.success('Staff deleted');
      setViewStaff(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting staff');
    }
  };

  // ----- Toggle / Increment -----
  const handleToggle = async (s) => {
    const msg = s.isActive === false ? 'Activate this staff member?' : 'Deactivate this staff member?';
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/staff/${s._id}/toggle`);
      toast.success('Status updated');
      fetchData();
      if (viewStaff && viewStaff._id === s._id) setViewStaff({ ...viewStaff, isActive: !(viewStaff.isActive !== false) });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };


  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Staff</h2>
          <p className="text-muted">List of staff - {staff.length}</p>
        </div>
        <div className="header-actions">
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff by name, ID, outlet, contact..."
            />
          </div>
          {canEdit && (
            <>
              <button className="btn btn-outline" onClick={downloadStaffTemplate} title="Download Excel template">
                <FileDown size={16} /> Template
              </button>
              <label className="btn btn-outline" title="Bulk upload staff from Excel" style={{ cursor: bulkUploading ? 'wait' : 'pointer' }}>
                <Upload size={16} /> {bulkUploading ? 'Uploading...' : 'Upload Staff'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBulkUpload}
                  disabled={bulkUploading}
                  style={{ display: 'none' }}
                />
              </label>
              <button className="btn btn-primary" onClick={openCreate}>
                <Plus size={16} /> Add Staff
              </button>
            </>
          )}
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Staff ID</th>
              <th>Staff Name</th>
              <th className="th-sortable" onClick={() => toggleSort('outlet')}>
                Outlet
                <span className="sort-carets">
                  <ChevronUp size={11} className={sortKey === 'outlet' && sortDir === 'asc' ? 'active' : ''} />
                  <ChevronDown size={11} className={sortKey === 'outlet' && sortDir === 'desc' ? 'active' : ''} />
                </span>
              </th>
              <th>Shift</th>
              <th className="th-sortable" onClick={() => toggleSort('salary')}>
                Salary
                <span className="sort-carets">
                  <ChevronUp size={11} className={sortKey === 'salary' && sortDir === 'asc' ? 'active' : ''} />
                  <ChevronDown size={11} className={sortKey === 'salary' && sortDir === 'desc' ? 'active' : ''} />
                </span>
              </th>
              <th>Actions</th>
              {canEdit && (
                <th className="th-sortable" onClick={() => toggleSort('status')}>
                  Status
                  <span className="sort-carets">
                    <ChevronUp size={11} className={sortKey === 'status' && sortDir === 'asc' ? 'active' : ''} />
                    <ChevronDown size={11} className={sortKey === 'status' && sortDir === 'desc' ? 'active' : ''} />
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredStaff.map((s) => (
              <tr key={s._id} className={s.isActive === false ? 'row-disabled' : ''}>
                <td><span className="badge outlet-id-badge">{s.staffId}</span></td>
                <td>
                  <div className="staff-name-cell">
                    {s.photo && <img src={`${API_BASE}/uploads/${s.photo}`} alt="" className="staff-avatar" />}
                    <strong>{s.name}</strong>
                  </div>
                </td>
                <td>{s.outlet?.name || '—'}</td>
                <td>{shiftLabel(s.shiftStart, s.shiftEnd)}</td>
                <td>₹{s.salary?.toLocaleString()}</td>
                <td>
                  {canEdit && (
                    <button className="btn btn-sm btn-outline btn-icon" onClick={() => openView(s)} title="Edit staff details">
                      <Edit2 size={13} />
                    </button>
                  )}
                </td>
                {canEdit && (
                  <td>
                    <button
                      type="button"
                      className={`pill-toggle ${s.isActive === false ? 'off' : 'on'}`}
                      onClick={() => handleToggle(s)}
                      title={s.isActive === false ? 'Activate' : 'Deactivate'}
                      aria-pressed={s.isActive !== false}
                    >
                      <span className="pill-toggle-label">{s.isActive === false ? 'OFF' : 'ON'}</span>
                      <span className="pill-toggle-knob" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {filteredStaff.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} className="empty-state">
                {staff.length === 0 ? 'No staff yet. Add your first staff member.' : 'No staff match your search.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Add Staff</h3>
              <div className="modal-header-actions">
                <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowModal(false)} title="Close">
                  <X size={16} />
                </button>
              </div>
            </div>
            <form onSubmit={handleCreate} className="form-grid">
              <StaffFields form={form} setForm={setForm} outlets={outlets} isEdit={false} />
              <div className="modal-actions full-width">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Add Staff'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewStaff && (
        <div className="modal-overlay">
          <div className={`modal ${viewEditMode ? 'modal-lg' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Staff Details</h3>
              <div className="modal-header-actions">
                {canEdit && !viewEditMode && (
                  <button className="btn btn-sm btn-outline btn-icon" onClick={startViewEdit} title="Edit">
                    <Edit2 size={15} />
                  </button>
                )}
                <button className="btn btn-sm btn-outline btn-icon" onClick={attemptCloseView} title="Close">
                  <X size={16} />
                </button>
              </div>
            </div>

            {!viewEditMode ? (
              <>
                <div className="staff-detail-view">
                  {viewStaff.photo && <img src={`${API_BASE}/uploads/${viewStaff.photo}`} alt={viewStaff.name} className="staff-photo" />}
                  {[
                    ['Staff ID', viewStaff.staffId],
                    ['Name', viewStaff.name],
                    ['Mobile', viewStaff.mobile ? `+91 ${viewStaff.mobile}` : ''],
                    ['Job Title', viewStaff.jobTitle],
                    ['Outlet', viewStaff.outlet?.name],
                    ['Aadhar Card', formatAadhar(viewStaff.aadharCard)],
                    ['Parent Aadhar', formatMultiAadhar(viewStaff.parentsAadharCard)],
                    ['Emergency Contact', viewStaff.emergencyContact],
                    ['Type', viewStaff.type],
                    ['Experience', viewStaff.experience],
                    ['Qualification', viewStaff.qualification],
                    ['Date of Joining', viewStaff.dateOfJoining?.split('T')[0]],
                    ['Shift', shiftLabel(viewStaff.shiftStart, viewStaff.shiftEnd)],
                    ['Salary', `₹${viewStaff.salary?.toLocaleString()}`],
                    ['Status', viewStaff.isActive === false ? 'Inactive' : 'Active'],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="detail-row">
                      <span className="detail-label">{label}</span>
                      <span className="detail-value">{value}</span>
                    </div>
                  ))}
                  {canEdit && (
                    <div className="detail-row">
                      <span className="detail-label">Delete user</span>
                      <span className="detail-value">
                        <a href="#delete" className="link-danger" onClick={(e) => { e.preventDefault(); handleDeleteStaff(viewStaff); }}>
                          Delete
                        </a>
                      </span>
                    </div>
                  )}
                  {[
                    ['Last Working Day', viewStaff.lastWorkingDay?.split('T')[0]],
                    ['Left Without Notice', viewStaff.leftWithoutNotice ? 'Yes' : ''],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="detail-row">
                      <span className="detail-label">{label}</span>
                      <span className="detail-value">{value}</span>
                    </div>
                  ))}
                  {viewStaff.incrementHistory?.length > 0 && (
                    <div className="increment-history">
                      <h4>Increment History</h4>
                      {viewStaff.incrementHistory.map((inc, i) => (
                        <div key={i} className="detail-row">
                          <span className="detail-label">{inc.date?.split('T')[0]}</span>
                          <span className="detail-value">₹{inc.oldSalary?.toLocaleString()} → ₹{inc.newSalary?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Face Registration */}
                {viewStaff.facePhotos?.length > 0 && (
                  <div style={{ margin: '12px 0 0', padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: faceRegStatus ? 10 : 0 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>Kiosk Face Recognition</span>
                        <span style={{ marginLeft: 10, fontSize: 12, color: viewStaff.faceDescriptor?.length ? '#16a34a' : '#b45309', fontWeight: 500 }}>
                          {viewStaff.faceDescriptor?.length ? '✓ Registered' : '⚠ Not registered'}
                        </span>
                      </div>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => registerFace(viewStaff)}
                        disabled={faceRegStatus === 'loading'}
                        style={{ flexShrink: 0 }}
                      >
                        {faceRegStatus === 'loading'
                          ? 'Processing…'
                          : viewStaff.faceDescriptor?.length
                            ? 'Re-register Face'
                            : 'Register Face'}
                      </button>
                    </div>
                    {faceRegStatus === 'loading' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#1d4ed8' }}>
                        <span style={{ width: 13, height: 13, border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }}></span>
                        {faceRegMessage}
                      </div>
                    )}
                    {faceRegStatus === 'success' && (
                      <div style={{ fontSize: 12, color: '#16a34a' }}>✓ {faceRegMessage}</div>
                    )}
                    {faceRegStatus === 'error' && (
                      <div style={{ fontSize: 12, color: '#dc2626' }}>✗ {faceRegMessage}</div>
                    )}
                  </div>
                )}

                <div className="modal-actions">
                  <button className="btn btn-outline" onClick={attemptCloseView}>Close</button>
                </div>
              </>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); saveViewEdit(); }} className="form-grid">
                <div className="form-group">
                  <label>Staff ID</label>
                  <input value={viewStaff.staffId} readOnly className="input-readonly" />
                </div>
                <div className="form-group" />
                <StaffFields form={viewForm} setForm={setViewForm} outlets={outlets} isEdit={true} />
                <div className="modal-actions full-width">
                  <button type="button" className="btn btn-outline" onClick={attemptCloseView}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={viewSaving}>
                    {viewSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
